'use client'

import { Icon } from '@dc-shared-web/components/base'
import {
  faCog,
  faFileSearch,
  faLock,
  faBriefcase,
  faChevronLeft,
} from '@document-crunch/pro-regular-svg-icons'

interface ErrorPageProps {
  errorType:
    | 'accessDenied'
    | 'notFound'
    | 'serverError'
    | 'projectEditorUnassigned'
  dashboardUrl?: string
}

/**
 * Error page component that displays different error states with custom messages and actions
 * @param errorType - Type of error to display ('accessDenied', 'notFound', or 'serverError')
 * @param dashboardUrl - URL to return to dashboard
 * @returns Error page component with icon, message and action button
 */
export function ErrorPage({ errorType, dashboardUrl }: ErrorPageProps) {
  const pageMap = {
    accessDenied: {
      title: 'Access Denied',
      description:
        "You don't have permission to view this crunch. Please contact your team admin to request access.",
      icon: (
        <Icon icon={faLock} className='tw-w-20 tw-h-20 tw-left-5 tw-top-5' />
      ),
      link: dashboardUrl,
      linkText: 'Return to dashboard',
      linkClassname: '',
    },
    notFound: {
      title: 'Crunch not found',
      description:
        'This crunch does not exist or was deleted. If you think this is a mistake, please contact support.',
      icon: (
        <Icon
          icon={faFileSearch}
          className='tw-w-20 tw-h-20 tw-left-5 tw-top-5'
        />
      ),
      link: `mailto:support@documentcrunch.com`,
      linkText: 'support@documentcrunch.com',
      linkClassname: 'tw--mt-3',
    },
    serverError: {
      title: 'Internal server error',
      description:
        'Oops! Something went wrong. Try refreshing this page. If the problem persists, contact us at',
      icon: (
        <>
          <Icon icon={faCog} size='2x' className='tw--mt-4' />
          <Icon icon={faCog} size='2x' className='tw-mt-4 tw-rotate-[25deg]' />
        </>
      ),
      link: `mailto:support@documentcrunch.com`,
      linkText: 'support@documentcrunch.com',
      linkClassname: 'tw--mt-3',
    },
    projectEditorUnassigned: {
      title: 'Nothing to show here yet!',
      description: `Don't worry, you just need to be assigned to a project first.`,
      icon: <Icon icon={faBriefcase} size='3x' />,
      link: '',
      linkText: '',
      linkClassname: '',
    },
  }

  return (
    <div className='dc-shared-web tw-h-full tw-w-full'>
      <div className='tw-h-full tw-w-full tw-bg-neutral-50'>
        <div className='tw-h-full tw-w-full tw-relative'>
          {dashboardUrl && (
            <a
              href={dashboardUrl}
              className='tw-box-border tw-inline-flex tw-items-center tw-justify-center tw-gap-[5px] tw-bg-neutral-100 tw-rounded tw-absolute tw-cursor-pointer tw-text-neutral-700 tw-px-2.5 tw-py-[7px] tw-left-[15px] tw-top-[15px] hover:tw-bg-neutral-100 hover:tw-text-neutral-700'
            >
              <div className='tw-inline-flex tw-items-center tw-justify-center tw-gap-[5px] tw-relative tw-font-medium tw-flex-[0_0_auto]'>
                <Icon
                  icon={faChevronLeft}
                  className='tw-w-4 tw-h-4 tw-text-neutral-700'
                />
                <div className='tw-relative tw-w-fit tw-mt-px'>Dashboard</div>
              </div>
            </a>
          )}
          <div className='tw-flex tw-h-full tw-w-full tw-justify-center tw-items-center'>
            <div className='tw-flex tw-flex-col tw-w-[400px] tw-items-center tw-gap-3 tw-relative'>
              <div className='tw-relative tw-w-[125px] tw-h-[125px] tw-bg-neutral-200 tw-rounded-[62.5px] tw-border-2 tw-border-solid tw-border-neutral-200 tw-flex tw-items-center tw-justify-center tw-bg-opacity-40'>
                {pageMap[errorType].icon}
              </div>
              <div className='tw-gap-2.5 tw-flex tw-flex-col tw-items-center tw-relative tw-self-stretch tw-w-full tw-flex-[0_0_auto]'>
                <div className='tw-relative tw-w-[380px] tw-font-semibold tw-text-neutral-700 tw-text-2xl tw-text-center tw-tracking-[0] tw-leading-[normal] tw--mt-px'>
                  {pageMap[errorType].title}
                </div>
                <div className='tw-gap-5 tw-flex tw-flex-col tw-items-center tw-relative tw-self-stretch tw-w-full tw-flex-[0_0_auto]'>
                  <p className='tw-relative tw-w-[365px] tw-ml-[-35px] tw-mr-[-35px] tw-font-normal tw-text-neutral-700 tw-text-sm tw-text-center tw--mt-px'>
                    {pageMap[errorType].description}
                  </p>
                </div>
                <div className={pageMap[errorType].linkClassname}>
                  <a
                    href={pageMap[errorType].link}
                    className='tw-text-blue-600 tw-text-sm tw-underline'
                  >
                    {pageMap[errorType].linkText}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
